import React from "react";
import ConfirmationModal from "./ConfirmationModal";

/**
 * Server + client timing breakdown for debugging (e.g. when server sets DEBUG_AI_TIMINGS=true).
 * Reuse anywhere you need to show the same timing UX after a fetch completes.
 *
 * @param {object} props
 * @param {boolean} props.open
 * @param {() => void} props.onClose
 * @param {string} [props.title]
 * @param {string} [props.contextLabel] — e.g. endpoint name shown under the title
 * @param {{ elapsedMs?: number, fromCache?: boolean } | null} props.server
 * @param {{ totalMs?: number, requestMs?: number, jsonMs?: number } | null} props.client
 */
function DebugTimingsModal({ open, onClose, title = "Request timing (debug)", contextLabel, server, client }) {
  const serverMs = server?.elapsedMs;
  const fromCache = Boolean(server?.fromCache);
  const totalMs = client?.totalMs;
  const requestMs = client?.requestMs;
  const jsonMs = client?.jsonMs;

  const message = (
    <div>
      {contextLabel ? (
        <p className="panel-hint" style={{ margin: "0 0 10px 0" }}>
          {contextLabel}
        </p>
      ) : null}
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
        <tbody>
          <tr style={{ borderBottom: "1px solid #e9ecef" }}>
            <td style={{ padding: "6px 8px 6px 0", color: "#6c757d" }}>Server</td>
            <td style={{ padding: "6px 0", color: "#212529", fontWeight: 600 }}>
              {serverMs != null ? `${serverMs} ms` : "—"}
              {fromCache ? " (cache)" : ""}
            </td>
          </tr>
          <tr style={{ borderBottom: "1px solid #e9ecef" }}>
            <td style={{ padding: "6px 8px 6px 0", color: "#6c757d" }}>Client total</td>
            <td style={{ padding: "6px 0", color: "#212529", fontWeight: 600 }}>{totalMs != null ? `${totalMs} ms` : "—"}</td>
          </tr>
          <tr style={{ borderBottom: "1px solid #e9ecef" }}>
            <td style={{ padding: "6px 8px 6px 0", color: "#6c757d" }}>Client fetch</td>
            <td style={{ padding: "6px 0" }}>{requestMs != null ? `${requestMs} ms` : "—"}</td>
          </tr>
          <tr>
            <td style={{ padding: "6px 8px 6px 0", color: "#6c757d" }}>Client JSON parse</td>
            <td style={{ padding: "6px 0" }}>{jsonMs != null ? `${jsonMs} ms` : "—"}</td>
          </tr>
        </tbody>
      </table>
      <p className="panel-hint" style={{ margin: "10px 0 0 0", fontSize: "12px" }}>
        Enable server timing with <code style={{ fontSize: "11px" }}>DEBUG_AI_TIMINGS=true</code>. Client times are measured in the browser until
        <code style={{ fontSize: "11px" }}> response.json()</code> finishes.
      </p>
    </div>
  );

  return (
    <ConfirmationModal
      open={open}
      title={title}
      message={message}
      mode="alert"
      confirmLabel="OK"
      onConfirm={() => {
        if (onClose) onClose();
      }}
      onCancel={() => {
        if (onClose) onClose();
      }}
    />
  );
}

export default DebugTimingsModal;
